import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { startOfDay, subHours, subMinutes } from 'date-fns';
import { EMPTY, catchError, interval } from 'rxjs';
import { RegistrationWrongCodeError } from '../errors/RegistrationWrongCodeError.js';

const ticketCollectionName = 'buchungsheldtickets';

interface BuchungsheldOTPTicket {
  _id: string;
  otp: string;
  userID: string;
  timestamp: Date;
}

export const useOTPOnly = async (otp: string) => {
  const col = await getRawCollection<BuchungsheldOTPTicket>(ticketCollectionName);
  const ticket = await col.findOne({ otp });
  if (!ticket) throw new RegistrationWrongCodeError('ticket not found for: ' + otp);
  const isValidOTP = await useOTP(ticket.userID, otp);
  if (!isValidOTP) throw new RegistrationWrongCodeError('otp not valid: ' + JSON.stringify(ticket));
  return ticket.userID;
};

export const useOTP = async (userID: string, otp: string) => {
  if (!userID) throw new RegistrationWrongCodeError('userID missing');
  if (!otp) throw new RegistrationWrongCodeError('otp missing');
  const col = await getRawCollection<BuchungsheldOTPTicket>(ticketCollectionName);
  const ticket = await col.findOne({ otp, userID });
  if (!ticket) return false;
  await col.deleteMany({ userID });
  if (ticket.timestamp < subMinutes(new Date(), 10)) return false;
  return true;
};

export const createOTP = async ({ userID }: { userID: string }) => {
  const col = await getRawCollection<BuchungsheldOTPTicket>(ticketCollectionName);
  let otp: string;

  // ? Do we want to disable old otps for the same userID?

  // ? Do we need better handling here? Or do we need user-scoped otps only?
  if ((await col.countDocuments({})) > 5000) throw new RegistrationWrongCodeError('too many otps present in DB');

  // make sure otps are generated unique
  do {
    otp = generateRandomFourDigitNumber();
  } while (await col.countDocuments({ otp }));

  await col.insertOne({ _id: A.getGloballyUniqueID(), otp, userID, timestamp: new Date() });
  return otp;
};

export const deleteOTP = async ({ userID, otp }: { userID: string; otp: string }) => {
  const col = await getRawCollection<BuchungsheldOTPTicket>(ticketCollectionName);
  await col.deleteMany({ userID, otp });
};

const generateRandomFourDigitNumber = () => {
  const randomNumber = Math.max(0, Math.min(9999, Math.floor(Math.random() * 10000)));
  const formattedNumber = randomNumber.toString().padStart(4, '0');
  return formattedNumber;
};

/**
 * regularly cleanup all expired otps in database
 * only cleans otps from the day before and created more than one hour ago
 */
export const registerOTPTokenCleanupScheduler = () => {
  const cleanup = async () => {
    const col = await getRawCollection<BuchungsheldOTPTicket>(ticketCollectionName);
    const lastDay = startOfDay(new Date());
    const oneHourAgo = subHours(new Date(), 1);

    const result = await col.deleteMany({
      $and: [{ timestamp: { $lt: lastDay } }, { timestamp: { $lt: oneHourAgo } }],
    });
    logger.info({ deletedCount: result.deletedCount }, 'deleted expired otps');
  };

  interval(1000 * 60 * 60 * 6) // run every 6 hours
    .pipe(
      catchError(error => {
        console.error('Error in OTP cleanup scheduler:', error);
        return EMPTY;
      }),
    )
    .subscribe(() => {
      cleanup().catch(error => {
        console.error('Error during OTP cleanup:', error);
      });
    });

  // run one time at server startup if in development mode
  if (baseConfig.get('DEPLOYMENT') === 'development')
    cleanup().catch(err => {
      logger.error({ err }, 'Error during OTP cleanup');
    });
};
